import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { Trash2, Upload, X, ChevronLeft, ChevronRight, FolderOpen, Folder, ChevronDown, ChevronUp, FileImage, User, Download } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const BASE_URL = process.env.REACT_APP_BACKEND_URL;

const formatSize = (bytes) => {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
};

const WeekendCallPhotos = () => {
  const [satFiles, setSatFiles] = useState([]);
  const [sunFiles, setSunFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState(null);
  const [openDay, setOpenDay] = useState(null);
  const [operators, setOperators] = useState([]);
  const [userRole, setUserRole] = useState("");

  const fetchAll = useCallback(async () => {
    try {
      const [sat, sun] = await Promise.all([
        axios.get(`${API}/weekend-files?day_group=saturday`),
        axios.get(`${API}/weekend-files?day_group=sunday`),
      ]);
      setSatFiles(sat.data);
      setSunFiles(sun.data);
    } catch (e) {
      console.error("Failed to fetch weekend files:", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
    // Fetch user role
    axios.get(`${API}/auth/me`).then(res => setUserRole(res.data?.role || "")).catch(() => {});
    // Fetch operators list
    axios.get(`${API}/team/users`).then(res => {
      const ops = (res.data || [])
        .filter(u => (u.role || '').toLowerCase().includes('scheduling'))
        .map(u => u.full_name || u.name || u.email)
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b));
      setOperators([...new Set(ops)]);
    }).catch(() => {});
  }, [fetchAll]);

  const openLightbox = (url, allUrls, index) => setLightbox({ url, allUrls, index });
  const closeLightbox = () => setLightbox(null);
  const navigateLightbox = (dir) => {
    if (!lightbox) return;
    const i = lightbox.index + dir;
    if (i >= 0 && i < lightbox.allUrls.length) setLightbox({ ...lightbox, url: lightbox.allUrls[i], index: i });
  };

  return (
    <>
      <div data-testid="weekend-call-sections">
        <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Saturdays & Sundays</h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <FileFolder
            title="Saturday's Call"
            dayGroup="saturday"
            files={satFiles}
            isOpen={openDay === "saturday"}
            onToggle={() => setOpenDay(openDay === "saturday" ? null : "saturday")}
            onRefresh={fetchAll}
            onOpenFile={openLightbox}
            allFileUrls={satFiles.map(f => f.url)}
            loading={loading}
            color="#2F5D8A"
            operators={operators}
            canDeleteAll={userRole === "Admin" || userRole === "Finance"}
          />
          <FileFolder
            title="Sunday's Call"
            dayGroup="sunday"
            files={sunFiles}
            isOpen={openDay === "sunday"}
            onToggle={() => setOpenDay(openDay === "sunday" ? null : "sunday")}
            onRefresh={fetchAll}
            onOpenFile={openLightbox}
            allFileUrls={sunFiles.map(f => f.url)}
            loading={loading}
            color="#8A5D2F"
            operators={operators}
            canDeleteAll={userRole === "Admin" || userRole === "Finance"}
          />
        </div>
      </div>

      {lightbox && (
        <div className="fixed inset-0 bg-black/85 z-[100] flex items-center justify-center" onClick={closeLightbox} data-testid="photo-lightbox">
          <button onClick={closeLightbox} className="absolute top-4 right-4 text-white/80 hover:text-white z-10" data-testid="lightbox-close"><X className="h-7 w-7" /></button>
          {lightbox.index > 0 && (
            <button onClick={(e) => { e.stopPropagation(); navigateLightbox(-1); }} className="absolute left-4 text-white/70 hover:text-white"><ChevronLeft className="h-10 w-10" /></button>
          )}
          <img src={`${BASE_URL}${lightbox.url}`} alt="" className="max-h-[85vh] max-w-[90vw] object-contain rounded-lg shadow-2xl" onClick={(e) => e.stopPropagation()} />
          {lightbox.index < lightbox.allUrls.length - 1 && (
            <button onClick={(e) => { e.stopPropagation(); navigateLightbox(1); }} className="absolute right-4 text-white/70 hover:text-white"><ChevronRight className="h-10 w-10" /></button>
          )}
          <div className="absolute bottom-4 text-white/60 text-sm">{lightbox.index + 1} / {lightbox.allUrls.length}</div>
        </div>
      )}
    </>
  );
};

const FileFolder = ({ title, dayGroup, files, isOpen, onToggle, onRefresh, onOpenFile, allFileUrls, loading, color, operators, canDeleteAll }) => {
  const [fileName, setFileName] = useState("");
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [selectedOperator, setSelectedOperator] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });
  const [showUpload, setShowUpload] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [deletingAll, setDeletingAll] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [uploadMsg, setUploadMsg] = useState(null);
  const inputRef = useRef(null);

  const handleDownloadAll = async () => {
    setDownloading(true);
    try {
      const res = await axios.get(`${API}/weekend-files/download-all?day_group=${dayGroup}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = url;
      const disposition = res.headers["content-disposition"] || "";
      const match = disposition.match(/filename="(.+)"/);
      link.download = match ? match[1] : `${dayGroup}_files.zip`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (e) { console.error("Download failed:", e); }
    finally { setDownloading(false); }
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0 || !fileName.trim() || !selectedOperator) return;
    setUploading(true);
    setUploadProgress({ current: 0, total: selectedFiles.length });
    setUploadMsg(null);
    let success = 0;
    for (let i = 0; i < selectedFiles.length; i++) {
      try {
        const fd = new FormData();
        fd.append("day_group", dayGroup);
        fd.append("name", fileName.trim());
        fd.append("operator", selectedOperator);
        fd.append("file", selectedFiles[i]);
        await axios.post(`${API}/weekend-files`, fd);
        success++;
      } catch (e) {
        console.error(`Upload failed for ${selectedFiles[i].name}:`, e);
      }
      setUploadProgress({ current: i + 1, total: selectedFiles.length });
    }
    setFileName("");
    setSelectedFiles([]);
    setSelectedOperator("");
    setShowUpload(false);
    setUploading(false);
    setUploadProgress({ current: 0, total: 0 });
    setUploadMsg({ type: "success", text: `${success} of ${selectedFiles.length} file(s) uploaded` });
    setTimeout(() => setUploadMsg(null), 4000);
    onRefresh();
  };

  const handleDelete = async (fileId) => {
    if (!window.confirm("Delete this file?")) return;
    try { await axios.delete(`${API}/weekend-files/${fileId}`); onRefresh(); }
    catch (e) { console.error(e); }
  };

  const handleDeleteAll = async () => {
    setDeletingAll(true);
    try {
      await axios.delete(`${API}/weekend-files/delete-all?day_group=${dayGroup}`);
      setShowDeleteConfirm(false);
      onRefresh();
    } catch (e) { console.error("Delete all failed:", e); }
    finally { setDeletingAll(false); }
  };

  const formatDate = (iso) => {
    if (!iso) return "";
    try { return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); }
    catch { return ""; }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-testid={`weekend-${dayGroup}-section`}>
      {/* Folder header */}
      <button onClick={onToggle} className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50/80 transition-colors" data-testid={`folder-toggle-${dayGroup}`}>
        <div className="flex items-center gap-2.5">
          {isOpen ? <FolderOpen className="h-5 w-5" style={{ color }} /> : <Folder className="h-5 w-5" style={{ color }} />}
          <div className="text-left">
            <h3 className="text-sm font-semibold text-gray-800">{title}</h3>
            <p className="text-[11px] text-gray-400">{files.length} file{files.length !== 1 ? "s" : ""}</p>
          </div>
        </div>
        {isOpen ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
      </button>

      {isOpen && (
        <div className="border-t border-gray-100">
          {/* Delete All Confirmation */}
          {showDeleteConfirm && (
            <div className="px-4 py-3 bg-[#C07070]/5 border-b border-[#C07070]/20" data-testid={`${dayGroup}-delete-confirm`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-[#C07070]">Are you sure you want to delete all {files.length} files?</p>
                  <p className="text-[11px] text-gray-500 mt-0.5">This action cannot be undone. Only files in {title} will be deleted.</p>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => setShowDeleteConfirm(false)} className="px-3 py-1.5 text-xs font-medium text-gray-500 bg-gray-100 hover:bg-gray-200 rounded-md" data-testid={`${dayGroup}-delete-cancel`}>Cancel</button>
                  <button onClick={handleDeleteAll} disabled={deletingAll} className="px-3 py-1.5 text-xs font-bold text-white bg-[#C07070] hover:bg-[#A05050] rounded-md disabled:opacity-50" data-testid={`${dayGroup}-delete-confirm-btn`}>{deletingAll ? "Deleting..." : "Yes, Delete All"}</button>
                </div>
              </div>
            </div>
          )}
          {/* Upload bar */}
          <div className="px-3 py-2 bg-gray-50/50 border-b border-gray-100">
            {!showUpload ? (
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                  {files.length > 0 && (
                    <button
                      onClick={handleDownloadAll}
                      disabled={downloading}
                      className="flex items-center gap-1 px-2.5 py-1 text-[11px] font-medium rounded-md bg-[#6B8F62]/15 text-[#6B8F62] hover:bg-[#6B8F62]/25 transition-colors disabled:opacity-50"
                      data-testid={`${dayGroup}-download-all`}
                    >
                      <Download className="h-3 w-3" /> {downloading ? "Preparing ZIP..." : "Download All as ZIP"}
                    </button>
                  )}
                  {canDeleteAll && files.length > 0 && (
                    <button
                      onClick={() => setShowDeleteConfirm(true)}
                      className="flex items-center gap-1 px-2.5 py-1 text-[11px] font-medium rounded-md bg-[#C07070]/10 text-[#C07070] hover:bg-[#C07070]/20 transition-colors"
                      data-testid={`${dayGroup}-delete-all`}
                    >
                      <Trash2 className="h-3 w-3" /> Delete All Files
                    </button>
                  )}
                </div>
                <button
                  onClick={() => setShowUpload(true)}
                  className="flex items-center gap-1 px-2.5 py-1 text-[11px] font-medium rounded-md transition-colors ml-auto"
                  style={{ backgroundColor: `${color}15`, color }}
                  data-testid={`${dayGroup}-upload-toggle`}
                >
                  <Upload className="h-3 w-3" /> Upload File
                </button>
              </div>
            ) : (
              <div className="space-y-2" data-testid={`${dayGroup}-upload-form`}>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    placeholder="Property address..."
                    value={fileName}
                    onChange={(e) => setFileName(e.target.value)}
                    className="flex-1 px-2.5 py-1.5 text-xs border border-gray-200 rounded-lg bg-white focus:ring-1 focus:ring-gray-300 outline-none"
                    data-testid={`${dayGroup}-name-input`}
                  />
                  <button
                    onClick={() => inputRef.current?.click()}
                    className="px-2.5 py-1.5 text-xs border border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-gray-400 transition-colors whitespace-nowrap"
                    data-testid={`${dayGroup}-choose-files-btn`}
                  >
                    {selectedFiles.length > 0 ? `${selectedFiles.length} file(s) selected` : "Choose files"}
                  </button>
                  <input ref={inputRef} type="file" accept="image/*,.heic,.heif" multiple className="hidden" onChange={(e) => { setSelectedFiles(Array.from(e.target.files)); e.target.value = ""; }} data-testid={`${dayGroup}-file-input`} />
                </div>
                <div className="flex items-center gap-2">
                  <select
                    value={selectedOperator}
                    onChange={(e) => setSelectedOperator(e.target.value)}
                    className="flex-1 px-2.5 py-1.5 text-xs border border-gray-200 rounded-lg bg-white focus:ring-1 focus:ring-gray-300 outline-none"
                    data-testid={`${dayGroup}-operator-select`}
                  >
                    <option value="">Select operator...</option>
                    {operators.map(op => (
                      <option key={op} value={op}>{op}</option>
                    ))}
                  </select>
                  <button
                    onClick={handleUpload}
                    disabled={uploading || !fileName.trim() || selectedFiles.length === 0 || !selectedOperator}
                    className="px-3 py-1.5 text-xs font-medium text-white rounded-lg disabled:opacity-40"
                    style={{ backgroundColor: color }}
                    data-testid={`${dayGroup}-upload-btn`}
                  >
                    {uploading ? `${uploadProgress.current}/${uploadProgress.total}` : "Save"}
                  </button>
                  <button onClick={() => { setShowUpload(false); setFileName(""); setSelectedFiles([]); setSelectedOperator(""); }} className="text-xs text-gray-400 hover:text-gray-600">Cancel</button>
                </div>
                {/* Upload progress bar */}
                {uploading && uploadProgress.total > 0 && (
                  <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-300"
                      style={{ width: `${(uploadProgress.current / uploadProgress.total) * 100}%`, backgroundColor: color }}
                    />
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Upload message */}
          {uploadMsg && (
            <div className={`mx-3 mt-2 px-3 py-2 rounded-lg text-xs font-medium ${uploadMsg.type === "success" ? "bg-[#EDF3EC] border border-[#C5D9C1] text-[#4A7A56]" : "bg-[#F3ECEC] border border-[#DFC4C4] text-[#8B4545]"}`} data-testid={`${dayGroup}-upload-msg`}>
              {uploadMsg.text}
            </div>
          )}

          {/* File list */}
          <div className="max-h-[320px] overflow-y-auto">
            {loading ? (
              <div className="py-6 text-center"><div className="animate-spin rounded-full h-4 w-4 border-b-2 mx-auto" style={{ borderColor: color }}></div></div>
            ) : files.length === 0 ? (
              <div className="py-8 text-center text-gray-400 text-xs" data-testid={`${dayGroup}-empty`}>Empty folder</div>
            ) : (
              files.map((f, idx) => (
                <div key={f.id} className="flex items-center gap-2 px-4 py-2 border-b border-gray-50 last:border-0 hover:bg-gray-50/50 transition-colors group" data-testid={`file-row-${f.id}`}>
                  <FileImage className="h-4 w-4 text-gray-400 flex-shrink-0" />
                  <button
                    onClick={() => onOpenFile(f.url, allFileUrls, idx)}
                    className="flex-1 text-left text-xs font-medium text-gray-700 hover:text-[#2F5D8A] truncate transition-colors"
                    data-testid={`file-open-${f.id}`}
                  >
                    {f.name}
                  </button>
                  <span className="text-[10px] text-gray-400 flex-shrink-0">{formatSize(f.size)}</span>
                  {f.operator && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#D1A054]/10 text-[#8A6A2E] flex-shrink-0 hidden sm:inline-flex items-center gap-0.5" data-testid={`file-operator-${f.id}`}>
                      <User className="h-2.5 w-2.5" />
                      {f.operator}
                    </span>
                  )}
                  <span className="text-[10px] text-gray-300 flex-shrink-0 hidden md:inline">{formatDate(f.created_at)}</span>
                  <button
                    onClick={() => handleDelete(f.id)}
                    className="p-1 text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all flex-shrink-0"
                    data-testid={`file-delete-${f.id}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default WeekendCallPhotos;
